import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { TextField, Button, Typography, Alert, Box } from "@mui/material";
import "./PaymentPage.css";
import BASE_URL from "../../config/apiConfig";
import { requestFCMToken } from "../../utils/FirebaseUtils";

const PaymentPage = () => {
  const location = useLocation();
  const {
    farmName,
    totalPrice,
    cropType,
    sowingDate,
    paymentFrequency,
    cropName,
    cropCode,
    fieldArea,
    farm_id,
    mapImageUrl,
  } = location.state || {};
  const [cookies] = useCookies(["user_auth_token"]);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  const [email, setEmail] = useState(cookies.user_email || "");
  const [userName, setUserName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailError, setEmailError] = useState(false);
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [fcmToken, setFcmToken] = useState(null);
  const [company, setCompany] = useState("");
  const [gst, setGst] = useState("");
  const [summaryDetails, setSummaryDetails] = useState([]);
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponFeedback, setCouponFeedback] = useState(null); // { type: 'success' | 'error', message: string }
  const [total, setTotal] = useState(0.0);
  const [discountAmount, setDiscountAmount] = useState(0); // 💰
  const [errors, setErrors] = useState({
    user_name: false,
    email: false,
    phone_number: false,
    address: false,
    city: false,
    state: false,
    zip: false,
  });

  useEffect(() => {
    if (!totalPrice) return;

    const total = parseFloat(totalPrice) || 0.0;
    const base = parseFloat((total / 1.18).toFixed(2));
    const gstAmount = parseFloat((total - base).toFixed(2));

    setTotal(total.toFixed(2));

    const summary = [
      `Base Price (Excl. GST): ₹${base.toFixed(2)}`,
      `GST (18%): ₹${gstAmount.toFixed(2)}`,
      `Total Price (Incl. GST): ₹${total.toFixed(2)}`,
    ];

    setSummaryDetails(summary);
  }, [totalPrice]);

  const fetchUserDetails = async (user_email) => {
    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user_email }),
      });
      const data = await response.json();
      if (response.status === 200 && data.user) {
        setUserDetails(data.user);
        setUserName(data.user.name);
        setPhoneNumber(data.user.phone);
      }
    } catch (err) {
      console.error("User fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const initializeFCMToken = async () => {
    try {
      const token = await requestFCMToken();
      setFcmToken(token);

      // Register the token with backend
      await fetch(`${BASE_URL}/api/devices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
    } catch (error) {
      console.error("Error initializing FCM token:", error);
    }
  };

  useEffect(() => {
    if (cookies.user_email) {
      fetchUserDetails(cookies.user_email);
    } else {
      setLoading(false);
    }
    initializeFCMToken();
  }, [cookies.user_email]);

  const handleCouponApply = async () => {
    if (!couponCode) return;

    const normalizedCoupon = couponCode.trim().toUpperCase();

    // 🚫 Prevent re-applying the same coupon
    if (appliedCoupon && appliedCoupon === normalizedCoupon) {
      setCouponFeedback({
        type: "info",
        message: `Coupon "${normalizedCoupon}" is already applied.`,
      });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/apply-coupon/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          coupon_code: normalizedCoupon,
          subtotal: total, // price before discount
        }),
      });

      const result = await response.json();

      const basetotal = parseFloat(totalPrice) || 0.0;
      const base = parseFloat((total / 1.18).toFixed(2));
      const gstAmount = parseFloat((total - base).toFixed(2));

      if (result.success) {
        setSummaryDetails([
          `Base Price (Excl. GST): ₹${base.toFixed(2)}`,
          `GST (18%): ₹${gstAmount.toFixed(2)}`,
          `Sub Total Price (Incl. GST): ₹${basetotal.toFixed(2)}`,
          `Discount (${result.discount_percent}%): -₹${result.discount_amount}`,
          `Total Price (After Discount): ₹${result.total_after_discount.toFixed(2)}`,
        ]);

        setTotal(result.total_after_discount.toFixed(2));
        setDiscountAmount(result.discount_amount); // 💰 track discount amount
        setAppliedCoupon(normalizedCoupon); // ✅ track coupon
        setCouponCode(""); // ✅ clear input
        setCouponFeedback({ type: "success", message: result.message });
      } else {
        setCouponFeedback({ type: "error", message: result.message });
      }
    } catch (error) {
      console.error("Coupon apply error:", error);
      setCouponFeedback({
        type: "error",
        message: "Something went wrong applying the coupon.",
      });
    }
  };

  const handleProceedToPay = async () => {
    if (!userDetails || !farmName) {
      alert("Missing required field data");
      return;
    }
    console.log(discountAmount);

    setSpinnerLoading(true);

    const payload = {
      UID: process.env.REACT_APP_UID,
      CropCode: cropCode,
      FieldName: farmName,
      PaymentType: paymentFrequency,
      amount: total,
      email,
      planName: cropType,
      farm_id,
      discountAmount,
    };

    try {
      const amount = Math.round(total * 100); // Convert ₹ to paise

      // 1. Create Razorpay order FIRST
      const orderResponse = await fetch(`${BASE_URL}/api/payment/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount,
          discountAmount,
          currency: "INR",
          receipt: `receipt_${Date.now()}`,
          userData: {
            name: userDetails.user_name,
            email,
            phone: userDetails.phone_number,
          },
          planDetails: {
            crop_name: cropName,
            crop_type: cropType,
            field_area: fieldArea,
            price: totalPrice,
            payment_frequency: paymentFrequency,
          },
        }),
      });

      const order = await orderResponse.json();

      const options = {
        key: "rzp_test_gHih3yGs0quJo5",
        amount,
        currency: "INR",
        name: "Farmology India",
        description: `${cropName} | ${cropType} | ${fieldArea} ha`,
        image:
          "https://farmologyindia.com/wp-content/uploads/elementor/thumbs/cropped-frmlgy-logo-qr7lic6cxgrollie5a58ze7z8a35cxz8b3zso6egs4.png",
        order_id: order.id,
        handler: async (response) => {
          try {
            // 2. Validate payment
            const validateResponse = await fetch(
              `${BASE_URL}/api/payment/validate`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  discountAmount,
                  appliedCoupon,
                  razorpay_order_id: order.id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  farm_id: farm_id,
                  customer_details: {
                    name: userName,
                    phone: phoneNumber,
                    email,
                  },
                  plan_details: {
                    crop_name: cropName,
                    crop_type: cropType,
                    field_area: fieldArea,
                    price: totalPrice,
                    payment_frequency: paymentFrequency,
                    sowing_date: sowingDate,
                  },
                  address,
                  city,
                  state,
                  zip,
                  gst,
                  company,
                }),
              },
            );

            const result = await validateResponse.json();

            if (result.success) {
              // 3. Now submit the field AFTER payment success
              const fieldResponse = await fetch(
                `${BASE_URL}/api/user/farm-management/submit-field`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(payload),
                },
              );

              const fieldResult = await fieldResponse.json();

              if (fieldResult.success && fieldResult.field_id) {
                // 1. Trigger stats
                fetch(`${BASE_URL}/api/user/stats/`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ field_id: fieldResult.field_id }),
                })
                  .then(() => {
                    // 2. After stats, trigger sentinel remarks
                    return fetch(
                      `${BASE_URL}/api/user/farm-management/openai-sentinel-remarks`,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ fieldId: fieldResult.field_id }),
                      },
                    );
                  })
                  .then((remarksRes) => remarksRes.json())
                  .then((remarks) => {
                    console.log("Sentinel remarks:", remarks);
                    // optionally update UI with remarks
                  })
                  .catch((err) => {
                    console.error(
                      "Error triggering Sentinel stats or remarks:",
                      err,
                    );
                  });
              }

              if (!fieldResult.success) {
                alert(
                  fieldResult.message ||
                    "Field submission failed after payment.",
                );
                return;
              }

              // Optional: Device subscription
              // await fetch(`${BASE_URL}/api/deviceswithemail`, {
              //   method: "POST",
              //   headers: { "Content-Type": "application/json" },
              //   body: JSON.stringify({
              //     email,
              //   }),
              // });

              navigate("/user/crop-health-management");
            } else {
              alert("Payment validation failed.");
              console.error("Validation failed:", result);
            }
          } catch (err) {
            console.error("Payment validation error:", err);
            alert("Something went wrong during payment validation.");
          } finally {
            setSpinnerLoading(false);
          }
        },
        prefill: {
          name: userDetails.user_name,
          email,
          contact: userDetails.phone_number,
        },
        modal: {
          ondismiss: () => {
            setSpinnerLoading(false); // 💡 Stop spinner when user closes modal
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Error during payment:", err);
      alert("Something went wrong. Please try again.");
      setSpinnerLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="payment-page">
      {spinnerLoading && (
        <div className="loader-overlay">
          <div className="loader-content">
            <div className="spinner" />
            <h2>Processing Payment...</h2>
          </div>
        </div>
      )}

      <div className="checkout-container">
        <div className="user-details">
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "#333",
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginBottom: "20px",
            }}
          >
            User Details
          </Typography>
          <TextField
            fullWidth
            name="name"
            label="Name"
            placeholder="Name"
            required
            value={userName || ""}
            error={errors.user_name}
            helperText={errors.user_name ? "This field is required" : ""}
            InputProps={{
              readOnly: true, // This makes the field read-only
            }}
            sx={{
              mb: 2,
              backgroundColor: "#f0f0f0", // Make the background light gray to indicate it's blocked
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "gray" },
                "&.Mui-focused fieldset": { borderColor: "gray" },
              },
            }}
          />

          <TextField
            fullWidth
            name="email"
            label="Email"
            placeholder="Email address"
            required
            value={email}
            InputProps={{
              readOnly: true, // This makes the field read-only
            }}
            sx={{
              mb: 2,
              backgroundColor: "#f0f0f0", // Make the background light gray to indicate it's blocked
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "gray" },
                "&.Mui-focused fieldset": { borderColor: "gray" },
              },
            }}
          />

          <TextField
            fullWidth
            name="phone"
            label="Phone Number"
            placeholder="Phone"
            required
            value={phoneNumber || ""}
            InputProps={{
              readOnly: true, // This makes the field read-only
            }}
            sx={{
              mb: 2,
              backgroundColor: "#f0f0f0", // Make the background light gray to indicate it's blocked
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "gray" },
                "&.Mui-focused fieldset": { borderColor: "gray" },
              },
            }}
          />

          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "#333",
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginBottom: "40px",
              marginTop: "50px",
            }}
          >
            Address
          </Typography>
          <TextField
            fullWidth
            name="address"
            label="Address"
            placeholder="Street Address"
            required
            value={address}
            error={errors.address}
            helperText={errors.address ? "This field is required" : ""}
            onChange={(e) => setAddress(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#51833a" },
                "&.Mui-focused fieldset": { borderColor: "#51833a" },
              },
            }}
          />
          <TextField
            fullWidth
            name="city"
            label="City"
            placeholder="City"
            required
            value={city}
            error={errors.city}
            helperText={errors.city ? "This field is required" : ""}
            onChange={(e) => setCity(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#51833a" },
                "&.Mui-focused fieldset": { borderColor: "#51833a" },
              },
            }}
          />
          <TextField
            fullWidth
            name="state"
            label="State"
            placeholder="State"
            required
            value={state}
            error={errors.state}
            helperText={errors.state ? "This field is required" : ""}
            onChange={(e) => setState(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#51833a" },
                "&.Mui-focused fieldset": { borderColor: "#51833a" },
              },
            }}
          />
          <TextField
            fullWidth
            name="zip"
            label="Zip Code"
            placeholder="Zip Code"
            required
            value={zip}
            error={errors.zip}
            helperText={errors.zip ? "This field is required" : ""}
            onChange={(e) => setZip(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#51833a" },
                "&.Mui-focused fieldset": { borderColor: "#51833a" },
              },
            }}
          />
          <TextField
            fullWidth
            name="company"
            label="Company Name"
            placeholder="Company Name"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#51833a" },
                "&.Mui-focused fieldset": { borderColor: "#51833a" },
              },
            }}
          />
          <TextField
            fullWidth
            name="gst"
            label="GST"
            placeholder="GST"
            value={gst}
            onChange={(e) => setGst(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": { color: "#51833a" },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#51833a" },
                "&.Mui-focused fieldset": { borderColor: "#51833a" },
              },
            }}
          />
        </div>
        <div className="separator" />
        <div className="order-summary">
          <h3>Order Summary</h3>
          <div className="summary-details">
            {mapImageUrl && (
              <div style={{ marginTop: "0px" }}>
                <h4>Map Preview:</h4>
                <img
                  src={mapImageUrl}
                  alt="Farm Area Map"
                  style={{ width: "80%", height: "auto", borderRadius: "8px" }}
                />
              </div>
            )}
            {farmName && <p>Farm Name: {farmName}</p>}
            {cropType && (
              <p>
                Crop Type:{" "}
                {cropType === "plantation"
                  ? "Plantation"
                  : cropType === "field"
                    ? "Field"
                    : cropType}
              </p>
            )}
            {cropName && <p>Crop Name: {cropName}</p>}
            {fieldArea && <p>Total Area: {fieldArea} Hectare</p>}
            {sowingDate && (
              <p>
                {cropName === "Tea" ? "Pruning Date" : "Sowing Date"}:{" "}
                {sowingDate}
              </p>
            )}
            {paymentFrequency && (
              <p>Plan Duration: {paymentFrequency} Months</p>
            )}
            <hr />

            {/* Render Summary Details */}
            {summaryDetails.length > 0 && (
              <div
                style={{
                  padding: "1rem",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#f0fff4",
                  marginTop: "1rem",
                  maxWidth: "300px",
                }}
              >
                {summaryDetails.map((line, index) => {
                  const [label, value] = line.split(":");
                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight:
                          index === summaryDetails.length - 1
                            ? "600"
                            : "normal",
                        fontSize:
                          index === summaryDetails.length - 1 ? "18px" : "15px",
                        color:
                          index === summaryDetails.length - 1
                            ? "#2f855a"
                            : "#2d3748",
                        marginBottom: "8px",
                      }}
                    >
                      <span>{label.trim()}</span>
                      <span>{value.trim()}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Show the Map Image URL if it exists */}

          {/* Coupon Section */}
          {/* Coupon Section */}
          <div
            className="coupon-input"
            style={{ maxWidth: 400, marginTop: 20 }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 400,
                mx: "auto",
                mt: 4,
                px: { xs: 2, sm: 0 },
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <TextField
                size="small"
                fullWidth
                label="Apply Coupon"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                sx={{
                  "& label.Mui-focused": { color: "#51833a" },
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: "#51833a" },
                    "&.Mui-focused fieldset": { borderColor: "#51833a" },
                  },
                }}
                InputLabelProps={{ style: { fontSize: 13 } }}
                inputProps={{ style: { fontSize: 13 } }}
              />

              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={handleCouponApply}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 13,
                  py: 1,
                  backgroundColor: "#51833a",
                  "&:hover": {
                    backgroundColor: "#406b2e",
                  },
                }}
              >
                Apply
              </Button>

              {couponFeedback && (
                <Alert
                  severity={couponFeedback.type}
                  variant="outlined"
                  sx={{ fontSize: 13, py: 0.5 }}
                >
                  {couponFeedback.message}
                </Alert>
              )}

              {appliedCoupon && (
                <Typography
                  variant="body2"
                  sx={{ color: "#388e3c", fontWeight: 500, fontSize: 13 }}
                >
                  ✅ Coupon <strong>{appliedCoupon}</strong> applied
                  successfully.
                </Typography>
              )}
            </Box>
          </div>

          {/* Proceed to Payment Button */}
          <div className="proceed-button">
            <Button
              variant="contained"
              color="success"
              onClick={handleProceedToPay}
              fullWidth
            >
              Proceed to Pay
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
